import React, { useState } from 'react';
import { Tab, Tabs } from '@tovia/man-ui';
import { ModelPanels } from '../Panels/ModelPanels';
import PhotographerPanels from '../Panels/PhotographerPanels';
import { MoviePanels } from '../Panels/MoviePanels';
import { useIntl } from 'react-intl';
import { useSelector } from 'src/client/redux';
import { Movie } from 'src/@types/Movie';

type Props = {
  item?: Movie;
  accessMode: string | null;
  hasGalleryPermissions: boolean;
};

export const MovieDetails = (props: Props) => {
  const intl = useIntl();
  const { config } = useSelector((state) => state.app);
  const { item, accessMode, hasGalleryPermissions } = props;
  const [currentTab, setCurrentTab] = useState('movie');
  const parentSite = useSelector((state) => state.site);
  const { siteSubdomain } = useSelector((state) => state.app);
  const sites = useSelector((state) => state.sites.items);

  if (!item || !accessMode) return null;

  const allowedTabs = config.moviePage.tabs || ['movie', 'model', 'photographer'];
  const { hasDirectors } = config;
  const isCompilationSite = Boolean(config.compilation.params?.siteUUIDs);

  const tabs = [
    {
      name: intl.formatMessage(
        { id: 'movie.tabs.movie', defaultMessage: `Film: ${item.name}` },
        { movieName: item.name },
      ),
      value: 'movie',
      type: 'movie',
    },
    ...item.models.map((model) => ({
      name: intl.formatMessage(
        { id: 'movie.tabs.cast', defaultMessage: `Cast: ${item.name}` },
        { modelName: model.name },
      ),
      value: `model_${model.UUID}`,
      type: 'model',
    })),
  ];
  if (hasDirectors) {
    tabs.push(
      ...item.photographers.map((photographer) => ({
        name: intl.formatMessage(
          { id: 'movie.tabs.director', defaultMessage: `Director: ${item.name}` },
          { directorName: photographer.name },
        ),
        value: `director_${photographer.UUID}`,
        type: 'photographer',
      })),
    );
  }

  const tabsNode = (
    <Tabs flat className="panels-tabs">
      {tabs
        .filter((tab) => allowedTabs.includes(tab.type))
        .map((tab) => (
          <React.Fragment key={tab.value}>
            <Tab
              {...tab}
              active={currentTab === tab.value}
              onClick={() => {
                setCurrentTab(tab.value);
              }}
            />{' '}
          </React.Fragment>
        ))}
    </Tabs>
  );

  return (
    <div>
      {!item.isStaffSelection && !item.isIntimateSelection && tabsNode}
      {currentTab === 'movie' && <MoviePanels hasAccess={hasGalleryPermissions} gallery={item} />}
      {item.models.map((model) => {
        if (currentTab !== `model_${model.UUID}`) {
          return <span key={model.UUID} />;
        }

        let modelPath = model.path;
        if (item.siteUUID !== parentSite?.UUID && !isCompilationSite) {
          const found = sites.find((s) => s.UUID === item.siteUUID);
          if (found) {
            modelPath = `https://${siteSubdomain}.${found.domain}${model.path}`;
          }
        }

        return <ModelPanels model={{ ...model, path: modelPath }} key={model.UUID} />;
      })}
      {item.photographers.map((photographer) => {
        if (currentTab !== `director_${photographer.UUID}`) {
          return <span key={photographer.UUID} />;
        }

        let photographerPath = photographer.path;
        if (item.siteUUID !== parentSite?.UUID && !isCompilationSite) {
          const found = sites.find((s) => s.UUID === item.siteUUID);
          if (found) {
            photographerPath = `https://${siteSubdomain}.${found.domain}${photographer.path}`;
          }
        }

        return (
          <PhotographerPanels photographer={{ ...photographer, path: photographerPath }} key={photographer.UUID} />
        );
      })}
    </div>
  );
};
